/**
 * Billing domain — API types and fetch helpers.
 *
 * All mutating calls use credentials: "include" so the browser forwards the
 * HttpOnly session cookie automatically.
 */

const API_URL =
  typeof window === "undefined"
    ? (process.env.BACKEND_INTERNAL_URL ?? "http://localhost:8000")
    : "";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Plan {
  id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  name: string;
  /** Price in cents */
  price: number;
  interval: string; // "month" | "year" | "one_time"
  features: Record<string, unknown>;
  is_visible: boolean;
  /**
   * Spec 029 D2: `"plan"` es de la página de precios (Start, Plus, Pro…);
   * `null` es heredado o manual y no se vende en Planes.
   */
  tipo: "plan" | null;
  /** Solo planes con IA (029; en 022, los `telefono`): minutos incluidos por periodo. */
  minutos_incluidos: number | null;
  /** Solo planes con IA: precio del minuto extra, en céntimos. */
  precio_minuto_extra: number | null;
  /** Código ISO en minúsculas, p. ej. "eur". */
  moneda: string;
  orden: number;
  /**
   * País del plan (ISO 3166-1 alfa-2, spec 022 §21); `null` = plan por defecto
   * para los países sin plan propio. Opcional: un backend anterior no lo manda.
   */
  pais?: string | null;
  /** Spec 029: WhatsApp con IA y asistente de voz (Plus y Pro; Start no). */
  ia: boolean;
  /** Spec 029: los tickets de soporte del local entran con prioridad alta (Pro). */
  soporte_prioritario: boolean;
  /** Primera imagen del producto de Stripe (`https://`), o `null`. */
  imagen_url?: string | null;
}

export interface Subscription {
  id: string;
  org_id: string;
  user_id: string | null;
  /** Null en una prueba sin plan (spec 022). */
  plan_id: string | null;
  stripe_subscription_id: string;
  /** "active" | "canceled" | "past_due" | "trialing" | "unpaid" */
  status: string;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
}

export interface Invoice {
  id: string;
  org_id: string;
  stripe_invoice_id: string;
  /** Amount in cents */
  amount_paid: number;
  status: string;
  hosted_invoice_url: string | null;
  created_at: string;
}

/** Una factura propia de Zirvo (spec 035 §3.3, `GET …/invoices`). */
export interface FacturaLocal {
  id: string;
  numero: string;
  emitida_at: string;
  periodo_inicio: string | null;
  periodo_fin: string | null;
  /** Lo facturado, p. ej. «Zirvo Start». */
  concepto: string;
  /** Céntimos. */
  total: number;
  moneda: string;
  estado: "paid" | "open" | "void" | "uncollectible" | string;
  /** `false` mientras el PDF se genera: «Generando…» y la consulta reintenta. */
  pdf_disponible: boolean;
  /**
   * Spec 035 §7.4: una rectificativa lleva importes negativos y su etiqueta en
   * la tabla. Opcional mientras el backend no lo envíe: sin él, `factura`.
   */
  tipo?: "factura" | "rectificativa";
}

export interface SubscriptionDetail {
  subscription: Subscription | null;
  invoices: Invoice[];
}

export interface ActiveSubscriptionItem {
  subscription: string;
  subscription_type: string;
  user: string;
  renewal_date: string | null;
  signup_date: string;
  status: string;
}

export interface ActiveSubscriptionListResponse {
  items: ActiveSubscriptionItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface LimitUsage {
  limit: number;
  used: number;
  remaining: number;
  is_over_limit: boolean;
}

export interface OrgEntitlements {
  plan_id: string | null;
  plan_name: string | null;
  flags: Record<string, boolean>;
  limits: Record<string, number>;
  soft_limits: string[];
  usage: Record<string, LimitUsage>;
}

export interface ManualPlanInput {
  name: string;
  price: number;
  interval: "month" | "year" | "one_time";
  features: Record<string, unknown>;
  is_visible: boolean;
}

// ---------------------------------------------------------------------------
// Admin helpers
// ---------------------------------------------------------------------------

/**
 * Triggers a sync of Stripe Products/Prices into local DB. Admin only.
 */
export async function syncPlans(): Promise<{ synced_count: number }> {
  const res = await fetch(`${API_URL}/api/billing/sync-plans`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let message = "No se pudieron sincronizar los planes.";
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return (await res.json()) as { synced_count: number };
}

/**
 * Updates a plan's visibility. Admin only.
 */
export async function updatePlanVisibility(
  planId: string,
  isVisible: boolean
): Promise<Plan> {
  const res = await fetch(`${API_URL}/api/billing/plans/${planId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_visible: isVisible }),
  });
  if (!res.ok) {
    let message = "No se pudo actualizar el plan.";
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return (await res.json()) as Plan;
}

export async function createManualPlan(input: ManualPlanInput): Promise<Plan> {
  const res = await fetch(`${API_URL}/api/billing/plans`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = "No se pudo crear el plan.";
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return (await res.json()) as Plan;
}

// ---------------------------------------------------------------------------
// Public / user helpers
// ---------------------------------------------------------------------------

/**
 * Fetches plans. Admins see all; users see only visible plans. Con `orgId`,
 * solo los del país del local o, por tipo, los por defecto (spec 022 §21.3).
 */
export async function getPlans(cookieHeader?: string, orgId?: string | null): Promise<Plan[]> {
  try {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (cookieHeader) headers["Cookie"] = cookieHeader;
    const query = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";

    const res = await fetch(`${API_URL}/api/billing/plans${query}`, {
      method: "GET",
      credentials: "include",
      headers,
      cache: "no-store",
    });
    if (!res.ok) return [];
    return (await res.json()) as Plan[];
  } catch {
    return [];
  }
}

/**
 * Creates a Stripe Checkout Session and returns its URL.
 */
export async function createCheckoutSession(
  planId: string,
  orgId?: string | null
): Promise<{ url: string }> {
  const body: { plan_id: string; org_id?: string } = { plan_id: planId };
  if (orgId) {
    body.org_id = orgId;
  }
  
  const res = await fetch(`${API_URL}/api/billing/checkout`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = "No se pudo crear la sesión de pago.";
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return (await res.json()) as { url: string };
}

/**
 * Sesión del portal de cliente de Stripe; devuelve su URL (owner/admin).
 *
 * Con `flujo: "cancelar"` el portal abre directamente la cancelación al final
 * del periodo (spec 029 §8.6); si había una bajada programada, el servidor la
 * suelta antes. `rutaVuelta` es la ruta relativa de la app a la que vuelve el
 * portal (el servidor le añade `portal=vuelta`); sin ella, la de antes
 * (`Referer`, o Facturación). Lanza `BillingApiError` (409
 * `SUSCRIPCION_NO_ACTIVA`, `STRIPE_NO_DISPONIBLE`…).
 */
export async function createPortalSession(
  orgId: string,
  opciones?: { flujo?: "cancelar"; rutaVuelta?: string }
): Promise<{ url: string }> {
  const body: { org_id: string; flujo?: "cancelar"; ruta_vuelta?: string } = { org_id: orgId };
  if (opciones?.flujo) body.flujo = opciones.flujo;
  if (opciones?.rutaVuelta) body.ruta_vuelta = opciones.rutaVuelta;
  return billingRequest<{ url: string }>("/api/billing/portal", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Fetches the current subscription and invoice history for an org.
 */
export async function getOrgSubscription(
  orgId: string,
  cookieHeader?: string
): Promise<SubscriptionDetail> {
  try {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (cookieHeader) headers["Cookie"] = cookieHeader;

    const res = await fetch(`${API_URL}/api/billing/subscription/${orgId}`, {
      method: "GET",
      credentials: "include",
      headers,
      cache: "no-store",
    });
    if (!res.ok) return { subscription: null, invoices: [] };
    return (await res.json()) as SubscriptionDetail;
  } catch {
    return { subscription: null, invoices: [] };
  }
}

/** Resumen antiabuso del admin de plataforma (`GET /api/admin/anti-abuse/summary`, feature 026). */
export interface VentanaAntiabuso {
  dias: number;
  locales_desplazados: number;
  pruebas_cortadas_ficha: number;
  pruebas_cortadas_correo: number;
  tarjetas_repetidas: number;
  correos_desechables: number;
  minutos_web_gratis: number;
  llamadas: { hechas: number; confirmadas: number; sin_confirmar: number; fallidas: number };
}

export interface OrgDeSenal {
  org_id: string;
  nombre: string | null;
}

export interface FichaSospechosa {
  google_place_id: string;
  locales_en_conflicto: number;
  locales_que_llamaron: number;
  reclamaciones: number;
  locales: OrgDeSenal[];
}

export interface LocalSospechoso {
  org_id: string;
  nombre: string | null;
  reclamaciones: number;
  perdidas: number;
}

export interface CuentaSospechosa {
  user_id: string;
  email: string | null;
  reclamaciones: number;
}

export interface ResumenAntiabuso {
  generado_at: string;
  ventanas: VentanaAntiabuso[];
  senales_dias: number;
  fichas: FichaSospechosa[];
  locales: LocalSospechoso[];
  cuentas: CuentaSospechosa[];
}

/** Server Component: el resumen antiabuso, o `null` si falla (la página lo dice). */
export async function getResumenAntiabuso(cookieHeader?: string): Promise<ResumenAntiabuso | null> {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (cookieHeader) headers["Cookie"] = cookieHeader;
  try {
    const res = await fetch(`${API_URL}/api/admin/anti-abuse/summary`, {
      method: "GET",
      credentials: "include",
      headers,
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as ResumenAntiabuso;
  } catch {
    return null;
  }
}

export async function getActiveSubscriptions(
  params: {
    page?: number;
    pageSize?: number;
    status?: string;
    sort?: "renewal_asc" | "renewal_desc";
  } = {},
  cookieHeader?: string
): Promise<ActiveSubscriptionListResponse> {
  try {
    const search = new URLSearchParams();
    search.set("page", String(params.page ?? 1));
    search.set("page_size", String(params.pageSize ?? 20));
    if (params.status) search.set("status", params.status);
    search.set("sort", params.sort ?? "renewal_asc");

    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (cookieHeader) headers["Cookie"] = cookieHeader;

    const res = await fetch(`${API_URL}/api/billing/subscriptions/active?${search.toString()}`, {
      method: "GET",
      credentials: "include",
      headers,
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error("No se pudieron cargar las suscripciones activas.");
    }

    return (await res.json()) as ActiveSubscriptionListResponse;
  } catch {
    return {
      items: [],
      total: 0,
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
      total_pages: 1,
    };
  }
}

export async function getOrgEntitlements(orgId: string, cookieHeader?: string): Promise<OrgEntitlements | null> {
  try {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (cookieHeader) headers["Cookie"] = cookieHeader;
    const res = await fetch(`${API_URL}/api/billing/orgs/${orgId}/entitlements`, {
      method: "GET",
      credentials: "include",
      headers,
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as OrgEntitlements;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Formatting utilities
// ---------------------------------------------------------------------------

/** Formats an integer amount in cents as a locale currency string. */
export function formatCurrency(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

/** Maps a subscription status string to a human-readable label + color. */
export function statusBadge(status: string): {
  label: string;
  color: "green" | "yellow" | "red" | "gray";
} {
  switch (status) {
    case "active":
    case "trialing":
      return { label: status === "trialing" ? "Trial" : "Active", color: "green" };
    case "past_due":
      return { label: "Past due", color: "yellow" };
    case "canceled":
    case "unpaid":
      return { label: status === "canceled" ? "Canceled" : "Unpaid", color: "red" };
    case "trial_expired":
      return { label: "Trial expired", color: "red" };
    default:
      return { label: status, color: "gray" };
  }
}

// ---------------------------------------------------------------------------
// Prueba de autoservicio (spec 022 §13.1)
// ---------------------------------------------------------------------------

export type FasePrueba =
  | "sin_prueba"
  | "prueba"
  | "prueba_llamadas_agotadas"
  | "prueba_terminada"
  | "plan";

/** Espejo exacto de `EstadoPrueba` del backend (§6.4); fechas como string ISO. */
export interface EstadoPrueba {
  fase: FasePrueba;
  dias_restantes: number | null;
  trial_ends_at: string | null;
  /** Fecha local (YYYY-MM-DD) del primer cobro, si hay tarjeta y la prueba sigue. */
  fecha_cobro: string | null;
  minutos_usados: number;
  minutos_incluidos: number;
  tarjeta: boolean;
  telefono_contratado: boolean;
  aviso_dia_21_pendiente: boolean;
  retencion_hasta: string | null;
  pago_fallido: boolean;
  puede_empezar_plan: boolean;
  /** Feature 026: la prueba se cerró al abrirse (ficha de Google o correo ya usados en otro local). */
  prueba_repetida?: boolean;
  /** Feature 026: la tarjeta ya tuvo minutos de regalo en otro local; esta prueba tiene 0. */
  tarjeta_repetida?: boolean;
  /** Feature 026, 2.ª tanda: la ficha de Google de este local la tiene otro; se ofrece «Soy el dueño». */
  ficha_reclamable?: boolean;
  /** Feature 026, 2.ª tanda: el dueño verificado de la ficha se la llevó; este local perdió la prueba. */
  ficha_reclamada?: boolean;
  /** Verify 2.ª tanda (L1): con `ficha_reclamable`, si verificar la ficha abre la prueba; si no, solo pasa la ficha. */
  reclamar_da_prueba?: boolean;
  /**
   * Spec 029: WhatsApp con IA y asistente de voz. `true` en prueba y en los
   * locales sin catálogo (heredados, demo, externos); `false` en Start y con la
   * prueba terminada.
   */
  ia: boolean;
  /** Spec 029: soporte prioritario (Pro, y la prueba: D5). */
  soporte_prioritario: boolean;
  /**
   * Spec 029: el plan del catálogo contratado; en prueba con tarjeta, el
   * elegido. `null` sin plan del catálogo (prueba sin tarjeta, plan heredado o
   * manual). La vista de Planes decide por él, no por `fase` (§6.5).
   */
  plan_actual: PrecioPlan | null;
  /** Spec 029: bajada programada para la renovación, si la hay. */
  cambio_programado: CambioProgramado | null;
  /** Spec 029: fecha local (YYYY-MM-DD) en que termina la suscripción cancelada al final del periodo. */
  cancela_el: string | null;
  /** Spec 029: fecha local (YYYY-MM-DD) en que se libera el número del asistente (plan sin IA). */
  numero_se_libera_el: string | null;
  /** Spec 029: el número (E.164) que se libera, si el local tiene uno. */
  numero_a_liberar: string | null;
  /**
   * Spec 029 E2: fecha local (YYYY-MM-DD) de la renovación de la suscripción
   * viva; en la prueba con tarjeta, la del primer cobro. `null` sin suscripción viva.
   */
  fecha_renovacion: string | null;
  /** Spec 029 E2: el local tiene un teléfono que perder al pasar a un plan sin IA (§6.6). */
  tiene_numero: boolean;
  /** Spec 029 E2: por qué terminó la suscripción; `null` si no terminó o no se distingue. */
  fin_motivo: FinMotivo | null;
}

/** Spec 029 E2: motivo del fin de la suscripción (`EstadoPrueba.fin_motivo`). */
export type FinMotivo = "prueba" | "cancelacion" | "impago";

/** Spec 029: la bajada que entra con la renovación (`EstadoPrueba.cambio_programado`). */
export interface CambioProgramado {
  plan: PrecioPlan;
  /** Fecha local (YYYY-MM-DD) en que entra el plan nuevo. */
  fecha: string;
}

/** Respuesta de «Llamarme» en la verificación del dueño de la ficha. */
export interface LlamadaVerificacionFicha {
  /** `+34 9•• •• •• 12`: el número completo no sale del servidor. */
  telefono_enmascarado: string;
  expira_at: string;
  llamadas_restantes: number;
}

/** Feature 028, F4f: la ficha elegida en el onboarding, comprobada antes de guardarla. */
export interface ComprobacionFicha {
  /** Otro local ya la tiene: guardarla corta la prueba (se ofrece «Soy el dueño»). */
  registrada: boolean;
  /** Google la da como negocio; una dirección nunca cuenta como ficha. */
  es_negocio: boolean;
}

export interface PrecioPlan {
  plan_id: string;
  /** Tal cual viene de Stripe. */
  nombre: string;
  /** Céntimos. */
  precio: number;
  moneda: string;
  intervalo: string;
  /** Solo con IA: minutos incluidos por periodo. */
  minutos_incluidos: number | null;
  /** Céntimos. Solo con IA. */
  precio_minuto_extra: number | null;
  /** Spec 029: WhatsApp con IA y asistente de voz (Plus y Pro; Start no). */
  ia: boolean;
  /** Spec 029: los tickets de soporte entran con prioridad alta (Pro). */
  soporte_prioritario: boolean;
  /** Spec 029: posición en el catálogo; a igual precio, decide subir o bajar (D3). */
  orden: number;
  /** Primera imagen del producto de Stripe (`https://`); cabecera de la tarjeta. */
  imagen_url?: string | null;
}

export interface PreciosPrueba {
  dias_prueba: number;
  minutos_prueba: number;
  /** Desde 029, el más barato de `planes` (el «desde X €» del acceso). */
  plataforma: PrecioPlan | null;
  /** Desde 029, igual que `planes`. */
  planes_plataforma: PrecioPlan[];
  /** Desde 029, siempre `null`: no hay línea de teléfono aparte. */
  telefono: PrecioPlan | null;
  /** País cuyos planes se devuelven; `null` si son los por defecto (§21.3). */
  pais?: string | null;
  /** Spec 029: el catálogo del país (Start, Plus, Pro), en su orden. */
  planes: PrecioPlan[];
}

export class BillingApiError extends Error {
  readonly status: number;
  /** `error_code` del sobre de error, o "UNKNOWN". */
  readonly code: string;
  /** `error.detail` del sobre: en un 422, los campos que fallan (spec 035 §7.3). */
  readonly detalle: unknown;

  constructor(message: string, status: number, code: string, detalle: unknown = null) {
    super(message);
    this.name = "BillingApiError";
    this.status = status;
    this.code = code;
    this.detalle = detalle;
  }
}

function billingHeaders(cookieHeader?: string): HeadersInit {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (cookieHeader) headers["Cookie"] = cookieHeader;
  return headers;
}

async function billingRequest<T>(
  path: string,
  options: RequestInit = {},
  cookieHeader?: string
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    cache: "no-store",
    ...options,
    headers: { ...billingHeaders(cookieHeader), ...(options.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; error_code?: string | null; detail?: unknown };
      detail?: string | { msg?: string }[];
    };
    const detalle = Array.isArray(body.detail) ? body.detail[0]?.msg : body.detail;
    throw new BillingApiError(
      body.error?.message ?? detalle ?? `Request failed: ${res.status}`,
      res.status,
      body.error?.error_code ?? "UNKNOWN",
      body.error?.detail ?? body.detail ?? null
    );
  }
  // Un 204 no trae cuerpo (`DELETE …/plan-change`): `res.json()` lanzaría.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Facturas propias del local, más reciente primero (owner/admin; spec 035
 * §3.3). En el cliente lanza `BillingApiError`; para sembrar desde el
 * servidor, `getFacturasLocalInicial`.
 */
export async function getFacturasLocal(orgId: string, cookieHeader?: string): Promise<FacturaLocal[]> {
  const { facturas } = await billingRequest<{ facturas: FacturaLocal[] }>(
    `/api/billing/orgs/${orgId}/invoices`,
    { method: "GET" },
    cookieHeader
  );
  return facturas;
}

/** Igual que `getFacturasLocal`, pero `null` ante cualquier error (siembra del servidor). */
export async function getFacturasLocalInicial(
  orgId: string,
  cookieHeader?: string
): Promise<FacturaLocal[] | null> {
  try {
    return await getFacturasLocal(orgId, cookieHeader);
  } catch {
    return null;
  }
}

/** Una rectificativa (spec 035 §7.4); sin `tipo`, una factura normal. */
export function esRectificativa(factura: FacturaLocal): boolean {
  return factura.tipo === "rectificativa";
}

/** El importe a enseñar: el de una rectificativa siempre en negativo, lo mande como lo mande el servidor. */
export function importeFactura(factura: FacturaLocal): number {
  return esRectificativa(factura) ? -Math.abs(factura.total) : factura.total;
}

// ---------------------------------------------------------------------------
// Datos de facturación (spec 035 §7.3)
// ---------------------------------------------------------------------------

/** Estado del número de IVA en VIES según Stripe; `null` sin número. */
export type EstadoVies = "verified" | "pending" | "unverified" | "unavailable";

/** Los campos editables, tal y como viajan en el `PUT`. */
export interface EntradaDatosFacturacion {
  razon_social: string | null;
  email_facturacion: string | null;
  linea1: string | null;
  linea2: string | null;
  codigo_postal: string | null;
  ciudad: string | null;
  provincia: string | null;
  /** ISO 3166-1 alfa-2. */
  pais: string | null;
  vat_id: string | null;
}

/** `GET/PUT …/billing-details`: lo del cliente de Stripe más el estado VIES. */
export interface DatosFacturacion extends EntradaDatosFacturacion {
  vies: EstadoVies | null;
}

export const CAMPOS_DATOS_FACTURACION = [
  "razon_social",
  "email_facturacion",
  "linea1",
  "linea2",
  "codigo_postal",
  "ciudad",
  "provincia",
  "pais",
  "vat_id",
] as const satisfies readonly (keyof EntradaDatosFacturacion)[];

export type CampoDatosFacturacion = (typeof CAMPOS_DATOS_FACTURACION)[number];

/**
 * Los datos de facturación del local (owner/admin). Lanza `BillingApiError`:
 * 409 `BILLING_NO_CUSTOMER` si aún no hay cliente de Stripe (se rellenan al pagar).
 */
export async function getDatosFacturacion(orgId: string, cookieHeader?: string): Promise<DatosFacturacion> {
  return billingRequest<DatosFacturacion>(
    `/api/billing/orgs/${orgId}/billing-details`,
    { method: "GET" },
    cookieHeader
  );
}

/**
 * Guarda los datos en el cliente de Stripe y devuelve el estado (VIES incluido).
 * Idempotente. Lanza `BillingApiError`: 422 de formato (ver
 * `camposConErrorFacturacion`), 409 `BILLING_NO_CUSTOMER`.
 */
export async function guardarDatosFacturacion(
  orgId: string,
  datos: EntradaDatosFacturacion
): Promise<DatosFacturacion> {
  return billingRequest<DatosFacturacion>(`/api/billing/orgs/${orgId}/billing-details`, {
    method: "PUT",
    body: JSON.stringify(datos),
  });
}

/**
 * Lo escrito, como lo guarda el servidor: sin espacios sobrantes, vacío como
 * `null`, el país en mayúsculas y el IVA sin espacios, puntos ni guiones.
 */
export function normalizarDatosFacturacion(
  datos: Record<CampoDatosFacturacion, string | null | undefined>
): EntradaDatosFacturacion {
  const limpio = (valor: string | null | undefined) => {
    const texto = (valor ?? "").trim();
    return texto === "" ? null : texto;
  };
  const salida = Object.fromEntries(
    CAMPOS_DATOS_FACTURACION.map((campo) => [campo, limpio(datos[campo])])
  ) as unknown as EntradaDatosFacturacion;
  salida.pais = normalizarPais(salida.pais);
  salida.vat_id = limpio(salida.vat_id?.replace(/[\s.-]/g, "").toUpperCase());
  return salida;
}

/**
 * Los campos que señala un 422 del `PUT`. Del sobre de validación
 * (`detail: [{loc: ["body", "vat_id"]}]`) o de un error propio
 * (`detail: {campo}` o `{field}`). Un 422 con código propio y sin campo es el
 * del formato del IVA, la única validación de negocio (§7.3).
 */
export function camposConErrorFacturacion(error: unknown): CampoDatosFacturacion[] {
  if (!(error instanceof BillingApiError) || error.status !== 422) return [];
  const conocidos = new Set<string>(CAMPOS_DATOS_FACTURACION);
  const nombres: unknown[] = [];
  const { detalle } = error;
  if (Array.isArray(detalle)) {
    for (const item of detalle) {
      const loc = (item as { loc?: unknown } | null)?.loc;
      if (Array.isArray(loc)) nombres.push(...loc);
    }
  } else if (detalle && typeof detalle === "object") {
    const objeto = detalle as { campo?: unknown; field?: unknown };
    nombres.push(objeto.campo, objeto.field);
  }
  const campos = [...new Set(nombres.filter((n): n is CampoDatosFacturacion => typeof n === "string" && conocidos.has(n)))];
  if (campos.length === 0 && error.code !== "VALIDATION_ERROR") return ["vat_id"];
  return campos;
}

/** La URL del PDF de una factura (el proxy de Next reenvía `/api`). */
export function rutaPdfFactura(orgId: string, facturaId: string): string {
  return `/api/billing/orgs/${orgId}/invoices/${facturaId}/pdf`;
}

/**
 * Descarga el PDF de una factura en el navegador con el nombre que da el
 * servidor (`Zirvo-{numero}.pdf`). Lanza `BillingApiError`; un 409
 * `INVOICE_PDF_PENDING` significa que el PDF se está generando.
 */
export async function descargarPdfFactura(orgId: string, factura: FacturaLocal): Promise<void> {
  const res = await fetch(rutaPdfFactura(orgId, factura.id), {
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; error_code?: string | null };
    };
    throw new BillingApiError(
      body.error?.message ?? `Request failed: ${res.status}`,
      res.status,
      body.error?.error_code ?? "UNKNOWN"
    );
  }
  const disposicion = res.headers.get("Content-Disposition") ?? "";
  const nombre = /filename="?([^";]+)"?/.exec(disposicion)?.[1] ?? `Zirvo-${factura.numero}.pdf`;
  const url = URL.createObjectURL(await res.blob());
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Después del clic: revocar en el mismo tick corta la descarga en Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Estado de la prueba del local. `null` ante cualquier error. */
export async function getEstadoPrueba(
  orgId: string,
  cookieHeader?: string
): Promise<EstadoPrueba | null> {
  try {
    return await billingRequest<EstadoPrueba>(
      `/api/billing/orgs/${orgId}/trial`,
      { method: "GET" },
      cookieHeader
    );
  } catch {
    return null;
  }
}

/**
 * Precios públicos de la prueba y los planes, de `pais` si se da (§21.3; un
 * código inválido no viaja). `null` ante cualquier error.
 */
export async function getPreciosPrueba(
  cookieHeader?: string,
  pais?: string | null
): Promise<PreciosPrueba | null> {
  const codigo = normalizarPais(pais);
  try {
    return await billingRequest<PreciosPrueba>(
      codigo ? `/api/billing/public-prices?pais=${codigo}` : "/api/billing/public-prices",
      { method: "GET" },
      cookieHeader
    );
  } catch {
    return null;
  }
}

/**
 * Precios de la prueba y catálogo de planes en el país del local (spec 029
 * §13, `GET /api/billing/orgs/{org_id}/prices`), en una sola petición: el
 * servidor sabe el país del local. Para miembros del local y superadmin.
 * `null` ante cualquier error.
 */
export async function getPreciosDelLocal(
  orgId: string,
  cookieHeader?: string
): Promise<PreciosPrueba | null> {
  try {
    return await billingRequest<PreciosPrueba>(
      `/api/billing/orgs/${orgId}/prices`,
      { method: "GET" },
      cookieHeader
    );
  } catch {
    return null;
  }
}

/** Marca el aviso del día 21 como visto. Lanza `BillingApiError`. */
export async function marcarAvisoVisto(orgId: string): Promise<EstadoPrueba> {
  return billingRequest<EstadoPrueba>(`/api/billing/orgs/${orgId}/trial/notice-seen`, {
    method: "POST",
  });
}

/** Termina la prueba y cobra el plan hoy. Lanza `BillingApiError`. */
export async function empezarPlan(orgId: string): Promise<EstadoPrueba> {
  return billingRequest<EstadoPrueba>(`/api/billing/orgs/${orgId}/start-plan`, {
    method: "POST",
  });
}

/** Confirma la tarjeta a la vuelta de Stripe Checkout. Lanza `BillingApiError`. */
export async function confirmarTarjeta(
  orgId: string,
  sessionId: string
): Promise<EstadoPrueba> {
  return billingRequest<EstadoPrueba>(`/api/billing/orgs/${orgId}/card/confirm`, {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId }),
  });
}

/**
 * Feature 026, 2.ª tanda: «Soy el dueño». El servidor llama al teléfono público
 * de la ficha de Google en conflicto y dicta un código. Lanza `BillingApiError`
 * (`PLACE_CLAIM_NOT_AVAILABLE`, `PLACE_PHONE_UNAVAILABLE`, `PLACE_CLAIM_LIMIT`,
 * `PLACE_CLAIM_CALL_FAILED`).
 *
 * Con `googlePlaceId` (onboarding, feature 028 F4f): la ficha recién elegida y
 * aún sin guardar. Sin él (Planes): la que el local ya guardó.
 */
export async function pedirLlamadaVerificacionFicha(
  orgId: string,
  googlePlaceId?: string,
): Promise<LlamadaVerificacionFicha> {
  return billingRequest<LlamadaVerificacionFicha>(`/api/billing/orgs/${orgId}/place-claim/call`, {
    method: "POST",
    ...(googlePlaceId ? { body: JSON.stringify({ google_place_id: googlePlaceId }) } : {}),
  });
}

/**
 * Feature 028, F4f: ¿guardar esta ficha le cortaría la prueba al local? Solo
 * eso: del local que ya la tiene no llega nada. Lanza `BillingApiError` (403 si
 * no es owner/admin, 429 si se pasa de 60 por hora).
 */
export async function comprobarFicha(orgId: string, googlePlaceId: string): Promise<ComprobacionFicha> {
  return billingRequest<ComprobacionFicha>(`/api/billing/orgs/${orgId}/place-claim/check`, {
    method: "POST",
    body: JSON.stringify({ google_place_id: googlePlaceId }),
  });
}

/**
 * Confirma el código de la llamada: la ficha pasa a este local. Lanza
 * `BillingApiError` (`PLACE_CLAIM_CODE_INVALID`, `_EXPIRED`, `_EXHAUSTED`).
 */
export async function confirmarVerificacionFicha(orgId: string, codigo: string): Promise<EstadoPrueba> {
  return billingRequest<EstadoPrueba>(`/api/billing/orgs/${orgId}/place-claim/confirm`, {
    method: "POST",
    body: JSON.stringify({ codigo }),
  });
}

export type ModoCheckout = "tarjeta_prueba" | "plan_ahora";

/**
 * Crea la sesión de Checkout de la prueba y devuelve la URL de Stripe. Desde
 * 029, una sola línea: la del plan elegido. Lanza `BillingApiError` (422
 * `PLAN_NO_VALIDO` sin `planId` o con uno fuera del catálogo del país).
 */
export async function crearCheckoutPrueba(i: {
  orgId: string;
  modo: ModoCheckout;
  /** El plan del catálogo que se contrata (spec 029 D7). */
  planId: string;
  rutaVuelta: string;
}): Promise<string> {
  const body = { org_id: i.orgId, modo: i.modo, ruta_vuelta: i.rutaVuelta, plan_id: i.planId };
  const res = await billingRequest<{ url: string }>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res.url;
}

export interface PlanUpdateInput {
  is_visible?: boolean;
  /** `"plan"`: de la página de precios; `null`: sin tipo (heredado o manual). */
  tipo?: "plan" | null;
  /** Spec 029: con `tipo: "plan"` e IA, minutos y minuto extra son obligatorios (422 `PLAN_TELEFONO_INCOMPLETO`). */
  ia?: boolean;
  soporte_prioritario?: boolean;
  minutos_incluidos?: number | null;
  precio_minuto_extra?: number | null;
  orden?: number;
  /** `null` = plan por defecto (§21.4). */
  pais?: string | null;
}

/** Actualiza un plan (superadmin). Solo viajan los campos presentes. Lanza `BillingApiError`. */
export async function updatePlan(planId: string, input: PlanUpdateInput): Promise<Plan> {
  return billingRequest<Plan>(`/api/billing/plans/${planId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// Planes Start, Plus y Pro: cambio de plan (spec 029 §8.4, §13)
// ---------------------------------------------------------------------------

/**
 * Qué hizo `cambiarPlan`: `inmediato` (en prueba con tarjeta: cambia lo que se
 * cobrará), `portal` (subir: falta confirmarlo en Stripe, en `portal_url`),
 * `programado` (bajar: entra con la renovación) o `sin_cambio` (el plan que ya
 * tenía; anula una bajada programada).
 */
export type EfectoCambioPlan = "inmediato" | "programado" | "portal" | "sin_cambio";

export interface CambioPlanResponse {
  efecto: EfectoCambioPlan;
  /** Solo con `efecto === "portal"`: la confirmación de Stripe. Caduca a los 5 min sin uso. */
  portal_url: string | null;
  /** El estado tras el cambio, para `setQueryData(claves.billing.prueba(org))`. */
  estado: EstadoPrueba;
}

/**
 * Cambia el plan del local al `planId` del catálogo (owner/admin); el efecto lo
 * decide el servidor (`EfectoCambioPlan`). `rutaVuelta` es la ruta relativa a
 * la que vuelve el portal al subir; sin ella, Planes. Lanza `BillingApiError`
 * (409 `SUSCRIPCION_NO_ACTIVA`, `PAGO_PENDIENTE`, `CANCELACION_PROGRAMADA`,
 * `STRIPE_NO_DISPONIBLE`; 422 `PLAN_NO_VALIDO`): ninguno se arregla reintentando.
 */
export async function cambiarPlan(
  orgId: string,
  planId: string,
  rutaVuelta?: string
): Promise<CambioPlanResponse> {
  const body: { plan_id: string; ruta_vuelta?: string } = { plan_id: planId };
  if (rutaVuelta) body.ruta_vuelta = rutaVuelta;
  return billingRequest<CambioPlanResponse>(`/api/billing/orgs/${orgId}/plan-change`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Anula la bajada programada (owner/admin). Idempotente: sin nada programado,
 * tampoco falla. No anula una cancelación (eso es «Reactivar», en el portal).
 * Lanza `BillingApiError`.
 */
export async function anularCambioPlan(orgId: string): Promise<void> {
  await billingRequest<void>(`/api/billing/orgs/${orgId}/plan-change`, { method: "DELETE" });
}

/**
 * Relee la suscripción en Stripe y devuelve el estado (owner/admin; spec 029
 * §8.7): a la vuelta del portal, para enseñar el plan nuevo sin esperar al
 * webhook. Idempotente. Lanza `BillingApiError`.
 */
export async function refrescarSuscripcion(orgId: string): Promise<EstadoPrueba> {
  return billingRequest<EstadoPrueba>(`/api/billing/orgs/${orgId}/subscription/refresh`, {
    method: "POST",
  });
}

/**
 * ¿Pasar de `actual` a `destino` es subir? (spec 029 D3, la regla de
 * `prueba.es_subida` y de Stripe): más caro o, al mismo precio, después en el
 * orden del catálogo. Compara planes de la misma moneda (el catálogo de un
 * país la comparte). El mismo plan no es subida.
 */
export function esSubida(actual: PrecioPlan, destino: PrecioPlan): boolean {
  if (destino.precio !== actual.precio) return destino.precio > actual.precio;
  return destino.orden > actual.orden;
}

/**
 * El plan que se preselecciona (spec 029 D7): el más barato con IA, que es el
 * que ya incluye el teléfono (Plus); a igual precio, el primero del catálogo.
 * Sin ninguno con IA, el primero; con el catálogo vacío, `null`.
 */
export function planPorDefecto(planes: PrecioPlan[]): PrecioPlan | null {
  let elegido: PrecioPlan | null = null;
  for (const plan of planes) {
    if (plan.ia && (elegido === null || plan.precio < elegido.precio)) elegido = plan;
  }
  return elegido ?? planes[0] ?? null;
}

/** Lo que se recomienda en «Elige tu plan» (feature 034): por capacidad, no por nombre. */
export type PlanRecomendado = "plus" | "start";

/**
 * El plan recomendado de «Elige tu plan» (feature 034): `plus` es el de por
 * defecto (el más barato con IA); `start`, el más barato sin IA; cualquier
 * otro valor, el plan con ese id si es del catálogo (viene de Planes). Lo que
 * no se encuentra cae en `planPorDefecto`.
 */
export function planRecomendado(planes: PrecioPlan[], recomendado: string | null | undefined): PrecioPlan | null {
  if (recomendado === "start") {
    let elegido: PrecioPlan | null = null;
    for (const plan of planes) {
      if (!plan.ia && (elegido === null || plan.precio < elegido.precio)) elegido = plan;
    }
    if (elegido) return elegido;
  } else if (recomendado && recomendado !== "plus") {
    const porId = planes.find((p) => p.plan_id === recomendado);
    if (porId) return porId;
  }
  return planPorDefecto(planes);
}

/**
 * Importe en céntimos con la moneda del plan y el idioma activo (UI spec §7).
 * Sin decimales sobrantes; `{ decimales: 2 }` los fuerza (precio del minuto extra).
 */
export function formatearPrecio(
  cents: number,
  moneda: string,
  locale: string,
  opts?: { decimales?: 2 }
): string {
  const minimo = opts?.decimales ?? 0;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: (moneda || "EUR").toUpperCase(),
    minimumFractionDigits: minimo,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Fecha «24 de octubre» en el idioma activo; añade el año solo si no es el
 * actual (UI spec §7). Una fecha sin hora (YYYY-MM-DD) se lee como local.
 */
export function formatearFecha(iso: string, locale: string): string {
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const fecha = soloFecha
    ? new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
    : new Date(iso);
  const conAnio = fecha.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    ...(conAnio ? { year: "numeric" as const } : {}),
  }).format(fecha);
}

export type EstadoVisualPrueba = "normal" | "aviso" | "agotado" | "terminada" | "oculto";

/** La única derivación permitida en cliente (UI spec §3). */
export function estadoVisualPrueba(p: EstadoPrueba | null): EstadoVisualPrueba {
  if (!p || p.fase === "sin_prueba" || p.fase === "plan") return "oculto";
  if (p.fase === "prueba_terminada") return "terminada";
  if (p.fase === "prueba_llamadas_agotadas") return "agotado";
  if (
    (p.dias_restantes !== null && p.dias_restantes <= 7) ||
    // Con 0 minutos incluidos el umbral sería 0 y el chip saldría en aviso
    // desde el primer día: sin minutos no hay umbral de minutos.
    (p.minutos_incluidos > 0 && p.minutos_usados >= Math.ceil(0.8 * p.minutos_incluidos))
  ) {
    return "aviso";
  }
  return "normal";
}

export type EstadoVisualPlan = "normal" | "aviso" | "sin_ia" | "oculto";

/**
 * Los minutos de un plan del catálogo (UI spec 005 §5.3; la píldora de la
 * barra): `oculto` sin plan del catálogo o en la prueba (allí manda
 * `estadoVisualPrueba`); `sin_ia` con un plan sin IA (Start: sin minutos, sin
 * barra); `aviso` desde `ceil(0,8 × incluidos)` o con minutos extra; si no,
 * `normal`. Nunca rojo: pasarse no corta nada, se cobra. El extra se deriva
 * como en la técnica §7: `max(0, floor(usados) − incluidos)`.
 */
export function estadoVisualPlan(p: EstadoPrueba | null): EstadoVisualPlan {
  if (!p || !p.plan_actual || (p.fase !== "plan" && p.fase !== "sin_prueba")) return "oculto";
  if (!p.plan_actual.ia) return "sin_ia";
  const incluidos = p.minutos_incluidos;
  const extra = Math.max(0, Math.floor(p.minutos_usados) - incluidos);
  if (extra > 0 || (incluidos > 0 && p.minutos_usados >= Math.ceil(0.8 * incluidos))) return "aviso";
  return "normal";
}

/**
 * Donde se puede estar con la prueba terminada (spec 022 §13.2): elegir plan,
 * pedir ayuda y lo imprescindible de la propia cuenta. La única lista: la
 * usan el redirect del layout (carga completa) y el guard de `AppShell`
 * (navegación en cliente).
 */
const RUTAS_CON_PRUEBA_TERMINADA = [
  "/pricing",
  "/help",
  "/support",
  "/settings/profile",
  "/force-password-reset",
];

/** Planes del local: el destino con la prueba terminada. */
export function rutaPlanes(orgId: string): string {
  return `/orgs/${orgId}/pricing`;
}

/**
 * Una ruta de la app a la que se puede mandar a alguien: relativa, sin `//`
 * ni barra invertida (la misma regla que `ruta_vuelta` en el backend). Lo que
 * no lo es, `null`: nunca se redirige fuera de la app.
 */
export function rutaInternaSegura(valor: string | null | undefined): string | null {
  return valor && /^\/(?:[^/\\\s][^\s]*)?$/.test(valor) ? valor : null;
}

/**
 * «Elige tu plan» (feature 034): la única pantalla para dejar la tarjeta.
 * `siguiente` es adonde se va después (con tarjeta o con «Decidir más tarde»).
 */
export function rutaEligePlan(
  orgId: string,
  { recomendado, siguiente }: { recomendado?: string | null; siguiente: string },
): string {
  const query = new URLSearchParams();
  if (recomendado) query.set("recomendado", recomendado);
  query.set("siguiente", siguiente);
  return `/orgs/${orgId}/elige-plan?${query.toString()}`;
}

/** ¿`ruta` es «Elige tu plan» de algún local? (la barra, sin destinos, como el onboarding). */
export function esRutaEligePlan(ruta: string): boolean {
  return /^\/orgs\/[^/]+\/elige-plan(\/|$)/.test(ruta);
}

/** `url` con `query` añadida antes del `#`: `/a#b` + `x=1` → `/a?x=1#b`. */
export function urlConQuery(url: string, query: string): string {
  const [base, ...resto] = url.split("#");
  const hash = resto.length ? `#${resto.join("#")}` : "";
  return `${base}${base.includes("?") ? "&" : "?"}${query}${hash}`;
}

/** ¿`ruta` es alguno de los `prefijos` o cuelga de él? `/help/1` sí de `/help`; `/helpdesk` no. */
function rutaBajoAlguno(ruta: string, prefijos: readonly string[]): boolean {
  return prefijos.some((prefijo) => ruta === prefijo || ruta.startsWith(`${prefijo}/`));
}

/** ¿Se puede estar en `ruta` con la prueba terminada del local `orgId`? */
export function rutaPermitidaConPruebaTerminada(ruta: string, orgId: string): boolean {
  return rutaBajoAlguno(ruta, [rutaPlanes(orgId), ...RUTAS_CON_PRUEBA_TERMINADA]);
}

/**
 * Donde se puede estar con el onboarding obligatorio sin terminar: el propio
 * asistente y el cambio de contraseña forzado, que el `proxy` impone antes que
 * nada (sin él aquí, uno y otro se mandarían de vuelta en bucle). Cerrar
 * sesión no es una ruta: es una llamada al API y una carga de `/login`, que
 * está fuera de la app. La única lista: la usan el redirect del layout (carga
 * completa) y el guard de `AppShell` (navegación en cliente), igual que la de
 * la prueba terminada.
 */
const RUTAS_CON_ONBOARDING_PENDIENTE = ["/onboarding", "/force-password-reset"];

/** ¿Se puede estar en `ruta` con el onboarding obligatorio sin terminar? */
export function rutaPermitidaConOnboardingPendiente(ruta: string): boolean {
  return rutaBajoAlguno(ruta, RUTAS_CON_ONBOARDING_PENDIENTE);
}

/**
 * ¿Tiene que terminar el onboarding antes de usar la app?
 *
 * Mientras no esté completado. «Dejarlo para luego» (`onboarding_dismissed`)
 * sólo lo libra en un local sin prueba de autoservicio —los de antes, que
 * cerró el equipo—: con prueba (`fase` distinta de `sin_prueba`) no hay
 * forma de saltárselo. Sin estado legible (`null`) no obliga: mejor no
 * bloquear que encerrar a alguien por un fallo de lectura.
 */
export function onboardingObligatorio(
  onboarding: { onboarding_completed: boolean; onboarding_dismissed: boolean } | null,
  prueba: EstadoPrueba | null,
): boolean {
  if (!onboarding || onboarding.onboarding_completed) return false;
  const conPrueba = !!prueba && prueba.fase !== "sin_prueba";
  return conPrueba || !onboarding.onboarding_dismissed;
}

// ---------------------------------------------------------------------------
// Planes por país (spec 022 §21)
// ---------------------------------------------------------------------------

/** Países con planes propios previstos. Informativo: no limita `plans.pais`. */
export const PAISES_SOPORTADOS = ["ES", "DE", "AT", "CH", "EE"] as const;

/** Idioma sin región → país (`es` → ES, `de` → DE, `et` → EE). */
const IDIOMA_A_PAIS: Record<string, string> = { es: "ES", de: "DE", et: "EE" };

/** Código ISO 3166-1 alfa-2 en mayúsculas, o `null` si no lo es. */
export function normalizarPais(valor: string | null | undefined): string | null {
  const codigo = valor?.trim().toUpperCase() ?? "";
  return /^[A-Z]{2}$/.test(codigo) ? codigo : null;
}

/**
 * País de un locale del navegador (misma regla que `pais_de_locale`, §21.2):
 * con región, la región si es un país soportado (`de-CH` → CH, `en-GB` → null);
 * sin región, el país del idioma (`de` → DE, `et` → EE, `en` → null).
 */
export function paisDeLocale(locale: string | null | undefined): string | null {
  const partes = (locale ?? "").trim().replace(/_/g, "-").split("-");
  const idioma = partes[0]?.toLowerCase() ?? "";
  const region = partes.slice(1).find((p) => /^[A-Za-z]{2}$/.test(p));
  if (region) {
    const codigo = normalizarPais(region);
    return codigo && (PAISES_SOPORTADOS as readonly string[]).includes(codigo) ? codigo : null;
  }
  return normalizarPais(IDIOMA_A_PAIS[idioma]);
}

/** Nombre del país en el idioma activo (`Intl.DisplayNames`); el código si no lo hay. */
export function nombrePais(codigo: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}
